import { PROCYON_SESSION_HEADER_NAME } from 'Utils/Constants'
import { getUserInfo } from 'Utils/Helpers'
import { getSessionToken } from './sessionToken'

import axios from 'axios'

/**
 * @type {{
 *  status: "IDLE" | "UPDATING" | "READY"
 *  previousUpdate: number
 *  currentUpdate: number
 * count: number
 * }}
 */
var REFRESH_TOKEN_CACHE = {
  status: 'IDLE',
  count: 0,
  previousUpdate: 0,
  currentUpdate: 0
}

const REFRESH_TOKEN_ENDPOINT = location.hostname.replace('console', 'console.login')

const refreshToken = async () => {
  if (REFRESH_TOKEN_CACHE.status === 'IDLE') {
    REFRESH_TOKEN_CACHE.status = 'UPDATING'
    const user = getUserInfo()
    console.log('Refreshing token for user: ', user)
    const response = await axios.get(
      `https://${REFRESH_TOKEN_ENDPOINT}/refresh?env=${user.ObjectMeta.Tenant}`,
      {
        headers: {
          [PROCYON_SESSION_HEADER_NAME]: await getSessionToken()
        },
        withCredentials: true
      }
    )

    REFRESH_TOKEN_CACHE.status = 'READY'
    // After 4 minutes set the status to IDLE again so that token can be refreshed again
    // assuming token expiry time is 5 or more minutes
    // This is to avoid multiple refresh calls in a short span of time
    setTimeout(() => {
      REFRESH_TOKEN_CACHE.status = 'IDLE'
    }, 1000 * 60 * 4)
    return response.data
  }

  /**
   * If token is being refreshed wait for it to complete
   * Else, since token is not being refreshed, proceed with the request
   */
  if (REFRESH_TOKEN_CACHE.status === 'UPDATING') {
    await waitTokenRefresh()
  }
}

const waitTokenRefresh = (params) =>
  new Promise((resolve, reject) => {
    try {
      /**
       * If token is being refreshed wait for it to complete
       * Else, since token is not being refreshed, proceed with the request
       */
      if (REFRESH_TOKEN_CACHE.status === 'UPDATING') {
        const timerID = setInterval(() => {
          if (REFRESH_TOKEN_CACHE.status === 'READY') {
            resolve(params)
            clearInterval(timerID)
          }
        }, 200)
      } else {
        resolve(params)
      }
    } catch (error) {
      reject(error)
    }
  })

const check401Unauthorized = async () => {
  return await refreshToken()
}

/**
 *
 * @param {import('axios').AxiosRequestConfig} _config
 */
const tokenRequestInterceptor = async (_config) => await waitTokenRefresh(_config)

/**
 *
 * @param {import('axios').AxiosError} error
 * @param {import('axios').AxiosInstance} axInstance
 * @returns
 */
const tokenResponseInterceptor = (error, axInstance) => {
  return new Promise((resolve, reject) => {
    try {
      console.log('response interceptors: ', error, error.response)
      error.config.__retryCount = error.config.__retryCount || 0

      if (error.config.__retryCount > 3) {
        // If we've already retried once, reject the promise
        return reject(error)
      }
      console.log('Retry count: ', error.config.__retryCount)
      if (error?.message.includes('401')) {
        check401Unauthorized().then(() => {
          error.config.__retryCount += 1
          resolve(axInstance(error.config))
        })
      } else {
        reject(error)
      }
    } catch (error) {
      reject(error)
    }
  })
}

/**
 *  Creates an axios instance with interceptors to handle token refreshing
 * @param {import('axios').AxiosInstance} instance
 */
export const createTokenRefreshingAxiosInstance = (instance) => {
  instance.interceptors.request.use(tokenRequestInterceptor)
  instance.interceptors.response.use(
    (response) => response,
    async (e) => await tokenResponseInterceptor(e, instance)
  )
  return instance
}
